import { Locale } from "@/types/locale";
import { PropsWithClassName } from "@/types/ui";
import React from "react";
import { useLocale } from "next-intl";
import { EntityEditOrViewLink } from "@components/common/buttons/entity-edit-or-view-link";
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from "@components/ui/card";
import { FadeFailSafeImage } from "@components/ui/custom/fade-failsafe-image";
import { cn } from "@lib/utils";
import { EventSiteLink } from "@features/events/ui/event-site-link";
import { useTranslateProposalFields } from "@features/proposals/hooks/proposal-translate-fields";
import { proposalsTools } from "@features/proposals/proposals-tools";
import { SpeakerAreaProposalEntity } from "@features/proposals/types/proposal";
import { ActivityScopeBadge } from "@features/proposals/ui/badges/activity-scope-badge";
import { ProposalWorkflowStatusBadge } from "@features/proposals/ui/badges/proposal-workflow-status-badge";
import { MemberRoleBadge } from "@features/proposals/ui/member/member-role-badge";

/**
 * Functional component that renders a ProposalCard, representing a proposal's details,
 * status, and related actions in a card-based UI layout. The card adapts dynamically to
 * user context, proposal state, and localization preferences.
 *
 * Features:
 * - Displays proposal details, including title, workflow status, and event branding.
 * - Renders localized content using i18n utilities for titles, workflow statuses, and type values.
 * - Contains defensive rendering for incomplete or invalid proposal data.
 * - Switches action links between "edit" and "view" modes based on the proposal's closed status.
 * - Supports CSS Container Queries for responsive typography and layout.
 *
 * Props:
 * - `proposal` (SpeakerAreaProposalEntity): The proposal entity containing activity details, workflow status, and event data.
 * - `className` (string): Additional CSS classes to apply to the card for styling or layout adjustments.
 */
export const ProposalCard = ({
  proposal,
  className,
  isSimpleView,
}: PropsWithClassName & { proposal: SpeakerAreaProposalEntity; isSimpleView?: boolean }) => {
  // DEPS
  // useTranslateProposalFields centralizes i18n for proposal domain:
  // - translateTypeValue maps Activity.typeValue to a localized label.
  // - translateWorkflowStatus returns both label and description for a workflow status,
  //   using the convention `${status}_description` in messages to explain next steps.
  // - localizeFrom picks the correct locale variant from localized content.
  const { translateTypeValue, translateWorkflowStatus, localizeFrom } =
    useTranslateProposalFields();

  // Get current locale for OG image selection
  const locale = useLocale() as Locale;

  // DATA
  // Activity content can be absent for incomplete or inconsistent backend responses.
  // We guard against missing content to avoid rendering an unusable card.
  const content = proposal.activity?.content?.data;

  // Business rule: a closed proposal is considered immutable from the UI point of view.
  // We use this flag later to switch the action link between "edit" and "view" modes.
  // Implementation detail: relies on proposalsTools.filters.isClosedProposal
  // which checks if workflow.status ∈ ClosedStatuses.
  const isClosedProposal = proposalsTools.filters.isClosedProposal(proposal);

  // Extract OG images for the current locale, prioritizing them over the event logo
  const ogImages = proposalsTools.activityTools.extractOGImagesUrls(
    proposal.activity?.content,
    true, // deleteDefaultOg: exclude default OG when same for both locales
  );
  const currentLocaleOgImage = ogImages[locale]?.[0];

  // Determine which image to display: OG image takes priority, fallback to event logo
  const headerImage = currentLocaleOgImage || proposal.event?.content?.data?.logoUri?.iv;
  const imageAlt = currentLocaleOgImage
    ? localizeFrom(content?.title) || proposal.event?.hint || ""
    : proposal.event?.hint || "";

  // RENDERS
  // Defensive render: skip the card entirely if the activity has no title.
  // This prevents broken layout and misleading navigation targets.
  if (!content?.title) {
    return null;
  }

  return (
    // Note: @container classes enable CSS Container Queries (tailwind v4),
    // allowing responsive typography inside this card without relying solely on viewport.
    <Card className={cn("@container/card shadow-xs", isSimpleView && "!gap-2 !py-2", className)}>
      {!isSimpleView && (
        // Header banner with a subtle background pattern.
        // This visually separates the card and provides a place for the event logo if present.
        <div
          className={cn("relative -mt-6 flex min-h-32 w-full overflow-hidden rounded-t-xl px-4", {
            "bg-muted/50 dark:bg-muted/80": !currentLocaleOgImage,
          })}
        >
          {/* Business: prioritize OG image for the current locale, fallback to the event logo.
            FadeFailSafeImage fades-in on a load and renders nothing on error to keep the UI clean.
            OG images stretch to fill the entire container with absolute positioning, event logos remain centered. */}
          {headerImage && (
            <FadeFailSafeImage
              src={headerImage}
              alt={imageAlt}
              width={currentLocaleOgImage ? 800 : 100}
              height={currentLocaleOgImage ? 600 : 100}
              className={
                currentLocaleOgImage
                  ? "absolute inset-0 h-full w-full object-cover"
                  : "relative z-10 mx-auto my-auto bg-center"
              }
            />
          )}
          {/* Background pattern overlay - only shown when no OG image to avoid visual artifacts */}
          {!currentLocaleOgImage && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 rounded-t-xl opacity-50 [background:repeating-linear-gradient(135deg,rgba(0,0,0,0.06)_0_1px,transparent_2px_14px)] dark:opacity-25 dark:[background:repeating-linear-gradient(135deg,rgba(255,255,255,0.07)_0_1px,transparent_2px_14px)]"
            />
          )}
        </div>
      )}

      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          {/* Activity type (e.g., TALK/WORKSHOP) localized for the current user. */}
          <CardDescription>{translateTypeValue(proposal.activity?.typeValue)}</CardDescription>
          <div className="ml-auto flex gap-2">
            <MemberRoleBadge role={proposal.member?.role} />
            {/*  /!* Scope badge is shown only when the scope is non-regular (special visibility/priority cases).*/}
            {/*   This signals to the user that the proposal has non-default constraints. *!/*/}
            {proposal.activity?.scope !== "REGULAR" && (
              <ActivityScopeBadge scope={proposal.activity?.scope}></ActivityScopeBadge>
            )}
          </div>
        </div>

        {/* Localized activity title. Container-query typography scales with the card width. */}
        <CardTitle
          className={cn(
            "min-h-32 text-base font-semibold @[200px]/card:text-xl @[250px]/card:text-2xl",
            isSimpleView && "!min-h-auto",
          )}
        >
          {localizeFrom(content.title)}
        </CardTitle>
      </CardHeader>

      <CardFooter
        className={cn(
          "flex h-full flex-col items-start justify-end gap-6 text-sm",
          isSimpleView && "gap-2",
        )}
      >
        {/* Event link: helps the user navigate to the host event site.
           Both link and title are optional and gracefully degraded if missing. */}
        <div className="line-clamp-1 flex gap-2 font-medium">
          <EventSiteLink
            link={proposal.event?.content?.data?.siteUri}
            title={proposal.event?.content?.data?.title}
          />
        </div>

        {/* Current workflow status badge (e.g., SUBMITTED/REVIEW/REJECTED/ACCEPTED). */}
        <ProposalWorkflowStatusBadge proposal={proposal} mode="badge" />

        {/* Status description provides human-readable guidance on what happens next.
           Translation key pattern: proposal.fields.workflow.status.values.${status}_description */}
        <div className={cn("min-h-20 text-muted-foreground", isSimpleView && "!min-h-auto")}>
          {proposal.workflow?.status &&
            translateWorkflowStatus(proposal.workflow?.status).descriptionSpeaker}
        </div>
      </CardFooter>

      {!isSimpleView && (
        // Bottom action bar:
        // - If a proposal is closed: navigate to read-only "view".
        // - Otherwise: navigate to "edit" to allow updates.
        // ignoreRightsFromEntity: we rely on external routing/guards; a UI link does not derive rights from the entity here.
        // disableReturnInUrl: prevents adding a return URL param to keep a cleaner navigation history. */}
        <div className="relative -mb-6 flex w-full justify-center border-t bg-muted/50 px-4 py-2 dark:bg-muted/80">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 rounded-t-xl opacity-50 [background:repeating-linear-gradient(135deg,rgba(0,0,0,0.06)_0_1px,transparent_2px_14px)] dark:opacity-25 dark:[background:repeating-linear-gradient(135deg,rgba(255,255,255,0.07)_0_1px,transparent_2px_14px)]"
          />
          <EntityEditOrViewLink
            className="w-full"
            mode={isClosedProposal ? "view" : "edit"}
            entity={proposal}
            variant="link"
            ignoreRightsFromEntity
            disableReturnInUrl
          />
        </div>
      )}
    </Card>
  );
};
